// src/components/AddTipoForm.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import "animate.css";
import "../css/addclienteform.css";

import { getClienteByCodigo, buscarClientes } from "../helpers/rutaClientes";
import { getTramites } from "../helpers/rutaTramites";
import { getTipoByCliente, upsertTipoByCliente } from "../helpers/rutaTipos";

/* ---------------- utils ---------------- */
const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const normComercial = (v) => {
  const s = String(v ?? "").trim().toUpperCase();
  return s === "SI" ? "SI" : "NO";
};

/* ---------------- initial form ---------------- */
const initialBase = {
  codtipo: "",
  codcli: "",
  fectipo: todayLocalISO(),
  codtramite: "",
  codapoderado: 1,
  honorario: "",
  comercial: "NO",
};

export default function AddTipoForm({ setShow }) {
  const [formValues, setFormValues] = useState(initialBase);
  const [showSuccess, setShowSuccess] = useState(false);

  const [ui, setUi] = useState({
    clienteDesc: "",
    tramiteDesc: "",
    apoderadoDesc: "DRA. PAULA TORRES",
  });

  const setField = useCallback((name, value) => {
    setFormValues((p) => ({ ...p, [name]: value }));
  }, []);

  /* ---------------- Trámites catálogo ---------------- */
  const [tramites, setTramites] = useState({ data: [], loading: true });

  useEffect(() => {
    getTramites()
      .then((list) => setTramites({ data: Array.isArray(list) ? list : [], loading: false }))
      .catch(() => setTramites({ data: [], loading: false }));
  }, []);

  const tramiteMap = useMemo(() => {
    const map = {};
    (tramites.data || []).forEach((t) => {
      const key = String(t.codtramite ?? t.costramite ?? "");
      map[key] = t?.tramite || "";
    });
    return map;
  }, [tramites.data]);

  useEffect(() => {
    const desc = tramiteMap[String(formValues.codtramite)] || "";
    setUi((p) => ({ ...p, tramiteDesc: desc }));
  }, [formValues.codtramite, tramiteMap]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  /* ---------------- Cliente por código + cargar tipo existente ---------------- */
  const [buscandoCod, setBuscandoCod] = useState(false);
  const reqSeqCod = useRef(0);

  const fetchClienteByCod = useCallback(async (codcli) => {
    const mySeq = ++reqSeqCod.current;

    setBuscandoCod(true);
    try {
      const resp = await getClienteByCodigo(codcli);
      if (mySeq !== reqSeqCod.current) return;

      const cli = resp?.cliente ?? resp;
      setUi((p) => ({ ...p, clienteDesc: cli?.razonsocial || "No encontrado" }));
    } catch (err) {
      if (mySeq !== reqSeqCod.current) return;

      const status = err?.response?.status;
      const msg =
        status === 401 || status === 403
          ? "Sin permiso (rol)"
          : status === 404
          ? "No encontrado"
          : "Error consultando cliente";

      setUi((p) => ({ ...p, clienteDesc: msg }));
    } finally {
      if (mySeq === reqSeqCod.current) setBuscandoCod(false);
    }
  }, []);

  const fetchTipoByCliente = useCallback(async (codcli) => {
    try {
      const r = await getTipoByCliente(codcli);
      const tipo = r?.tipo;
      if (!tipo) return;

      // si existe, cargamos datos del registro único
      setFormValues((p) => ({
        ...p,
        codtipo: tipo.codtipo ?? "",
        codcli: String(tipo.codcli ?? codcli),
        fectipo: tipo.fectipo ? String(tipo.fectipo).slice(0, 10) : p.fectipo,
        codtramite: String(tipo.codtramite ?? ""),
        codapoderado: 1,
        honorario: String(tipo.honorario ?? ""),
        comercial: normComercial(tipo.comercial),
      }));
    } catch {
      // si no existe, ok (se creará con upsert)
      setFormValues((p) => ({
        ...p,
        codtipo: "",
        codapoderado: 1,
      }));
    }
  }, []);

  /* ---------------- Modal buscar cliente (0 -> modal) ---------------- */
  const [showCliModal, setShowCliModal] = useState(false);
  const [qCli, setQCli] = useState("");
  const [cliOpts, setCliOpts] = useState([]);
  const [loadingCli, setLoadingCli] = useState(false);
  const [selIdx, setSelIdx] = useState(0);
  const reqSeqQ = useRef(0);
  const inputRef = useRef(null);

  const openCliModal = useCallback(() => {
    setShowCliModal(true);
    setQCli("");
    setCliOpts([]);
    setSelIdx(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const closeCliModal = useCallback(() => setShowCliModal(false), []);

  const buscarCliente = useCallback(
    async (rawOverride) => {
      const raw = String(rawOverride ?? formValues.codcli ?? "").trim();

      // ✅ Regla Fox: vacío o "0" -> modal
      if (raw === "" || raw === "0") {
        openCliModal();
        return;
      }

      const codcli = Number(raw);
      if (!Number.isFinite(codcli) || codcli <= 0) {
        openCliModal();
        return;
      }

      await fetchClienteByCod(codcli);
      await fetchTipoByCliente(codcli);
    },
    [formValues.codcli, fetchClienteByCod, fetchTipoByCliente, openCliModal]
  );

  // debounce búsqueda textual en modal
  useEffect(() => {
    if (!showCliModal) return;

    const q = qCli.trim();
    if (q.length < 2) {
      setCliOpts([]);
      setSelIdx(0);
      return;
    }

    const mySeq = ++reqSeqQ.current;
    const t = setTimeout(async () => {
      setLoadingCli(true);
      try {
        const list = await buscarClientes(q, 50);
        if (mySeq !== reqSeqQ.current) return;

        const arr = Array.isArray(list) ? list : [];
        setCliOpts(arr);
        setSelIdx(0);
      } catch {
        if (mySeq !== reqSeqQ.current) return;
        setCliOpts([]);
        setSelIdx(0);
      } finally {
        if (mySeq === reqSeqQ.current) setLoadingCli(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [qCli, showCliModal]);

  const seleccionarCliente = useCallback(
    async (c) => {
      if (!c) return;
      const codcli = Number(c.codcli);

      setField("codcli", String(codcli));
      setUi((p) => ({ ...p, clienteDesc: c.razonsocial || "" }));
      closeCliModal();

      await fetchTipoByCliente(codcli);
    },
    [setField, closeCliModal, fetchTipoByCliente]
  );

  const onModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeCliModal();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdx((i) => Math.min(i + 1, Math.max(0, cliOpts.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = cliOpts[selIdx] || cliOpts[0];
      if (pick) seleccionarCliente(pick);
    }
  };

  /* ---------------- validate + submit ---------------- */
  const validate = () => {
    const codcli = Number(formValues.codcli || 0);
    const codtramite = Number(formValues.codtramite || 0);

    if (codcli <= 0) return "Seleccioná un cliente (0 abre buscador).";
    if (!formValues.fectipo) return "Ingresá la fecha.";
    if (codtramite <= 0) return "Seleccioná un trámite.";
    const comercial = normComercial(formValues.comercial);
    if (comercial !== "SI" && comercial !== "NO") return "Comercial debe ser SI o NO.";
    return "";
  };

  const buildPayload = () => {
    const payload = {
      fectipo: formValues.fectipo,
      codtramite: Number(formValues.codtramite),
      codapoderado: 1,
      honorario: String(formValues.honorario ?? "").trim(),
      comercial: normComercial(formValues.comercial),
    };
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) return alert(msg);

    const codcli = Number(formValues.codcli);

    try {
      const resp = await upsertTipoByCliente(codcli, buildPayload());

      
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);

        // ✅ blanquea form
        setFormValues({
          ...initialBase,
          fectipo: todayLocalISO(),   // vuelve a "hoy"
        });

        // ✅ blanquea descripciones
        setUi((p) => ({
          ...p,
          clienteDesc: "",
          tramiteDesc: "",
          // apoderadoDesc lo dejamos fijo
        }));

        // opcional: cerrar form si lo usás con setShow
        if (typeof setShow === "function") setShow(false);
      }, 900);

      const tipo = resp?.tipo;

      // guardamos codtipo si vino (por si se creó)
      if (tipo?.codtipo) setField("codtipo", tipo.codtipo);

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // si querés cerrar el form como AddMovForm:
        if (typeof setShow === "function") setShow(false);
      }, 900);
    } catch (err) {
      console.error("❌ Error guardando tipo:", err?.response?.status, err?.response?.data || err);
      alert("No se pudo guardar. Revisá consola / Network.");
    }
  };

  return (
    <>
      <div className="container">
        <form onSubmit={handleSubmit}>
          {/* fila 1 */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-2">
              <label>Código</label>
              <input className="form-control" value={formValues.codtipo || ""} disabled placeholder="Auto" />
            </div>

            <div className="form-group mt-3 col-sm-2">
              <label>Fecha Mov.</label>
              <input
                type="date"
                className="form-control"
                name="fectipo"
                value={formValues.fectipo}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-2">
              <label>Cliente</label>

              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  name="codcli"
                  value={formValues.codcli}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      buscarCliente(e.currentTarget.value);
                    }
                  }}
                  onBlur={(e) => {
                    const v = String(e.currentTarget.value ?? "").trim();
                    if (v === "0") buscarCliente(v);
                    else if (v) buscarCliente(v);
                  }}
                  min="0"
                />

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => buscarCliente()}
                  title="Buscar (0 abre modal)"
                  style={{
                    width: 44,
                    height: 38,
                    padding: 0,
                    fontSize: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {buscandoCod ? "…" : "🔎"}
                </button>
              </div>

              <small className="text-muted">
                <b>0</b> y Enter buscar
              </small>
            </div>

            <div className="form-group mt-3 col-sm-6">
              <label>Cliente (Apellido y Nombre)</label>
              <input className="form-control" value={ui.clienteDesc} readOnly />
            </div>
          </div>

          {/* fila 2 */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Trámite</label>
              <select
                className="form-control"
                name="codtramite"
                value={formValues.codtramite}
                onChange={handleChange}
                disabled={tramites.loading}
                required
              >
                <option value="">Elija opción</option>
                {(tramites.data || [])
                  .filter((x) => x?.activo !== false)
                  .sort((a, b) => (a.codtramite ?? a.costramite ?? 0) - (b.codtramite ?? b.costramite ?? 0))
                  .map((t) => {
                    const cod = t.codtramite ?? t.costramite;
                    return (
                      <option key={t._id} value={cod}>
                        {cod} - {t.tramite}
                      </option>
                    );
                  })}
              </select>
            </div>

            <div className="form-group mt-3 col-sm-5">
              <label>Trámite (Descripción)</label>
              <input className="form-control" value={ui.tramiteDesc} readOnly />
            </div>

            <div className="form-group mt-3 col-sm-2">
              <label>Apoderado</label>
              <input className="form-control" value={formValues.codapoderado} readOnly />
            </div>

            <div className="form-group mt-3 col-sm-2">
              <label>Apoderado (Desc.)</label>
              <input className="form-control" value={ui.apoderadoDesc} readOnly />
            </div>
          </div>

          {/* fila 3 */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-8">
              <label>Honorarios</label>
              <input
                type="text"
                className="form-control"
                name="honorario"
                value={formValues.honorario}
                onChange={handleChange}
                placeholder="Ej: DOS / $123.000 / lo que uses"
              />
            </div>

            <div className="form-group mt-3 col-sm-4">
              <label>Comercial</label>
              <select className="form-control" name="comercial" value={formValues.comercial} onChange={handleChange}>
                <option value="NO">NO</option>
                <option value="SI">SI</option>
              </select>
            </div>
          </div>

          <button type="submit" className="btn btn-dark mt-4 mb-3" id="button">
            Guardar
          </button>
        </form>
      </div>

      {/* MODAL BUSCAR CLIENTE */}
      <Modal show={showCliModal} onHide={closeCliModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Buscar cliente</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Apellido / Razón social</Form.Label>
            <Form.Control
              ref={inputRef}
              value={qCli}
              placeholder="Ej: garc"
              onChange={(e) => setQCli(e.target.value)}
              onKeyDown={onModalKeyDown}
            />
            <Form.Text className="text-muted">↑ ↓ para moverte • Enter selecciona • Esc cierra</Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {loadingCli && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Buscando…
              </div>
            )}

            {!loadingCli && qCli.trim().length >= 2 && !cliOpts.length && (
              <div className="text-muted">Sin resultados.</div>
            )}

            {!!cliOpts.length && (
              <ListGroup>
                {cliOpts.map((c, idx) => (
                  <ListGroup.Item
                    key={c.codcli}
                    action
                    active={idx === selIdx}
                    onClick={() => seleccionarCliente(c)}
                  >
                    <b>{c.codcli}</b> — {c.razonsocial}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeCliModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => seleccionarCliente(cliOpts[selIdx] || cliOpts[0])}
            disabled={!cliOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SUCCESS */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Guardado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}